import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Box, Button, MenuItem } from '@material-ui/core';

import NgpForm from '../../form/NgpForm';
import { SORT_ASC, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { AdminPages, buildPageId } from '../AdminLinkUtils';

import FormSection from '../../form/FormSection';
import { useDataService } from '../../../services/ApiDataService';
import WebProxyConfiguration from './WebProxyConfiguration';
import { useDetailStyles } from './AgentSettingsStyles';
import {
    AGENT_SSH_KEY_CREATED_AT_FIELD,
    AGENT_SSH_SELECTION_ENABLED_OPTION,
    AGENT_SSH_SELECTION_FIELD,
    AGENT_VIRTUAL_INTERFACE_WIRED_COUNT_FIELD,
    AGENT_VIRTUAL_INTERFACE_WIRELESS_COUNT_FIELD,
    AGENT_WEB_UI_SELECTION_ENABLED_OPTION,
    AGENT_WEB_UI_SELECTION_FIELD,
    AGENT_WEB_UI_USERNAME_VALUE_FIELD,
    AGENT_XMS_HOST_FIELD,
    AGENT_XMS_LOCATION_FIELD,
    BEACON_PACKETS_FIELD,
    SINGLE_SEM_FIELD,
    PACKET_CAPTURE_DECRYPTION_FIELD,
    WEB_PROXY_FIELD,
    WEB_PROXY_ID_FIELD,
    WEB_PROXY_NO_PROXY_OPTION,
    WEB_PROXY_SELECTION_FIELD,
    AGENT_GLOBAL_WIRED_AUTH_ENABLED,
    AGENT_SNMP_V3_AUTH_CIPHER_FIELD,
    AGENT_SNMP_V3_PRIV_CIPHER_FIELD,
    AGENT_SNMP_V3_AUTH_CIPHER_OPTIONS,
    AGENT_SNMP_V3_PRIV_CIPHER_OPTIONS,
    AGENT_SNMP_V3_TARGET_EDGE_FIELD,
} from './AgentSettingsConstants';
import {
    buildBeaconInterfaceOptions,
    buildBeaconPackets,
    buildValidationFieldMapper,
    buildWebProxy,
    handleAgentSettingsSaveError,
    prepareBeaconPackets,
    prepareWebProxy,
    prepareSettings,
} from './AgentSettingsHelper';
import NgpToggle from '../../form/NgpToggle';
import NgpSelect from '../../form/NgpSelect';
import ListFormField from '../../form/ListFormField';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import XmsLocationForm from './XmsLocationForm';
import AgentWebAccess from './AgentWebAccess';
import AgentSSHAccess from './AgentSSHAccess';
import AgentSnmp from './AgentSnmp';
import AgentServiceTests from './AgentServiceTests';
import { dateTimeFormatter } from '../../../utilities';
import LinkButton from '../../links/LinkButton';

const functionName = AdminPages.AgentSettings;
const readApiUrl = '/ipm/v1/admin/agents/globalAgentSettings';
const writeApiUrl = '/ipm/v1/admin/systemconfiguration/';
const webProxyReadApiUrl = '/ipm/v1/admin/webproxies/default';
const webProxyWriteApiUrl = '/ipm/v1/admin/webproxies';
const defaultValues = {
    [AGENT_WEB_UI_SELECTION_FIELD]: AGENT_WEB_UI_SELECTION_ENABLED_OPTION,
    [AGENT_WEB_UI_USERNAME_VALUE_FIELD]: 'pulse',
    [AGENT_SSH_SELECTION_FIELD]: AGENT_SSH_SELECTION_ENABLED_OPTION,
    [AGENT_SNMP_V3_AUTH_CIPHER_FIELD]: AGENT_SNMP_V3_AUTH_CIPHER_OPTIONS.getDefault(),
    [AGENT_SNMP_V3_PRIV_CIPHER_FIELD]: AGENT_SNMP_V3_PRIV_CIPHER_OPTIONS.getDefault(),
    [AGENT_SNMP_V3_TARGET_EDGE_FIELD]: true,
};
const beaconInterfaceOptions = buildBeaconInterfaceOptions();
const validationFieldMapper = buildValidationFieldMapper();
const agentCertificatesLocation = '/admin/certificate';
const voipConfigLocation = '/admin/voip';
const agentSSHTooltipBase = t`Entering a Passphrase and clicking the Generate SSH Key button will generate a new SSH Key for nPoints to use.`;
const agentSSHTooltipDefault = t`  The default SSH key is being used.`;

const xmsLocationColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`nPoint Server URL`,
        sortable: true,
        mapping: AGENT_XMS_HOST_FIELD,
        flex: 1,
    },
];

const initialXmsLocationSorter = [{ property: AGENT_XMS_HOST_FIELD, direction: SORT_ASC }];

function prepareSavedData(dataToSave, formData) {
    const putData = _.extend({}, dataToSave, { _id: formData._id });

    prepareBeaconPackets(putData);
    prepareWebProxy(putData, formData);
    prepareSettings(dataToSave, putData);

    return putData;
}

export default function AgentSettingsDetail() {
    const classes = useDetailStyles();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const { reset, setError, handleSubmit, watch } = methods;
    const breadcrumbs = [{ text: i18n._(t`nPoint Settings`) }];
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const usingBeaconPackets = watch(BEACON_PACKETS_FIELD);
    const isSingleSEM = watch(SINGLE_SEM_FIELD);
    const agentSSHTooltip = useRef(`${agentSSHTooltipBase}${agentSSHTooltipDefault}`);
    const [locationSelectionIsDirty, setLocationSelectionIsDirty] = useState(false);

    const postFindHandler = (response) => {
        buildBeaconPackets(response);

        const agentSSHTooltipCustom = response[AGENT_SSH_KEY_CREATED_AT_FIELD]
            ? t`The SSH key that is is being used was created on ${dateTimeFormatter(
                  response[AGENT_SSH_KEY_CREATED_AT_FIELD]
              )}`
            : agentSSHTooltipDefault;

        agentSSHTooltip.current = `${agentSSHTooltipBase}${agentSSHTooltipCustom}`;

        return dataService
            .get(webProxyReadApiUrl)
            .then((webProxyResponse) => _.extend({}, response, buildWebProxy(webProxyResponse)));
    };

    const { initialized, finding, formData, resetForm } = useFind({
        url: readApiUrl,
        postFindHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    const saveWebProxy = (putData) => {
        const webProxyId = _.get(putData, WEB_PROXY_ID_FIELD);
        if (_.get(putData, WEB_PROXY_SELECTION_FIELD) === WEB_PROXY_NO_PROXY_OPTION) {
            if (!_.isEmpty(webProxyId)) {
                return dataService.remove(`${webProxyWriteApiUrl}/${webProxyId}`);
            }

            return;
        }

        return !_.isEmpty(webProxyId)
            ? dataService.put(`${webProxyWriteApiUrl}/${webProxyId}`, _.get(putData, WEB_PROXY_FIELD))
            : dataService.post(webProxyWriteApiUrl, _.get(putData, WEB_PROXY_FIELD));
    };

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(dataToSave, formData);

        return formSaveService.save({
            url: writeApiUrl,
            saveFunc: () =>
                dataService
                    .put(`${writeApiUrl}/${putData._id}`, putData)
                    .then(() => saveWebProxy(putData))
                    .then(() => {
                        setLocationSelectionIsDirty(false);
                        resetForm();
                    }),
            dataToSave: putData,
            setError,
            validationFieldMapper,
            saveErrorHandler: handleAgentSettingsSaveError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const dirtyCheck = (isDirty) => isDirty || locationSelectionIsDirty;

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection title={<Trans>Smart Edge Monitoring</Trans>}>
                        {/* This setting has been pulled for the 3.11 release - may be revisited in a future release
                        <NgpSelect
                            fieldName={AGENT_SEM_DESTINATION_DEVICE_TYPE}
                            label={<Trans>SEM destination device type</Trans>}
                            className={classes.interfaceSelect}
                        >
                            {_.map(SEM_DESTINATION_DEVICE_TYPES, (value, key) => (
                                <MenuItem key={key} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                        */}
                        <NgpToggle
                            fieldName={PACKET_CAPTURE_DECRYPTION_FIELD}
                            label={<Trans>Enable Packet Capture Decryption</Trans>}
                        />
                        <NgpToggle
                            fieldName={BEACON_PACKETS_FIELD}
                            disabled={isSingleSEM}
                            label={<Trans>Default Smart Edge Monitoring for all nPoints</Trans>}
                        />

                        {usingBeaconPackets === true && (
                            <Box ml={4}>
                                <NgpSelect
                                    fieldName={AGENT_VIRTUAL_INTERFACE_WIRED_COUNT_FIELD}
                                    label={<Trans>Default Virtual Ethernet Interfaces</Trans>}
                                    className={classes.interfaceSelect}
                                >
                                    {_.map(beaconInterfaceOptions, (value, key) => (
                                        <MenuItem key={key} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>

                                <NgpSelect
                                    fieldName={AGENT_VIRTUAL_INTERFACE_WIRELESS_COUNT_FIELD}
                                    label={<Trans>Default Virtual Wi-Fi Interfaces</Trans>}
                                    className={classes.interfaceSelect}
                                >
                                    {_.map(beaconInterfaceOptions, (value, key) => (
                                        <MenuItem key={key} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                            </Box>
                        )}
                    </FormSection>

                    <FormSection title={<Trans>Wired Authentication</Trans>}>
                        <NgpToggle
                            fieldName={AGENT_GLOBAL_WIRED_AUTH_ENABLED}
                            label={<Trans>Use 802.1x Authentication on nPoints</Trans>}
                        />
                    </FormSection>

                    <WebProxyConfiguration formData={formData} />

                    <FormSection title={<Trans>nPoint Server URLs</Trans>}>
                        <ListFormField
                            fieldName={AGENT_XMS_LOCATION_FIELD}
                            dbSelected={_.get(formData, AGENT_XMS_LOCATION_FIELD)}
                            gridColumns={xmsLocationColumns}
                            formComponent={<XmsLocationForm />}
                            addButtonText={t`Add URL`}
                            selectedListSorter={initialXmsLocationSorter}
                            setSelectionIsDirty={setLocationSelectionIsDirty}
                        />
                    </FormSection>

                    <AgentWebAccess />

                    <AgentSSHAccess tooltip={agentSSHTooltip.current} />

                    <AgentSnmp />

                    <AgentServiceTests />

                    <FormSection title={<Trans>Certificates</Trans>}>
                        <Box mt={3}>
                            <LinkButton
                                label={<Trans>Go to Certificates</Trans>}
                                tooltip={t`Configure Certificates`}
                                url={agentCertificatesLocation}
                            />
                        </Box>
                    </FormSection>

                    <FormSection title={<Trans>VoIP Configuration</Trans>}>
                        <Box mt={3}>
                            <LinkButton
                                label={<Trans>Go to VoIP Configuration</Trans>}
                                tooltip={t`Configure VoIP`}
                                url={voipConfigLocation}
                            />
                        </Box>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
